class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      handleTapCartCaptureKit(this);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    handleTapCartCaptureKit(this);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  renderCartItemContents(parsedState) {
    this.productId = parsedState.id;
    const sectionsToRender = [
      {
        id: 'cart-drawer',
        selector: '#cart-drawer-reward-progress'
      },
      {
        id: 'cart-drawer',
        selector: '#CartDrawer cart-drawer-items',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];

    sectionsToRender.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();

    this.setUpsellFunctionality();
    this.setVariantSelectors();
  }

  setUpsellFunctionality() {
    this.upsellElement = this.querySelector('#cart-upsell');
    this.upsellPrev = this.querySelector('.cart-upsell-slide-button[name="prev"]');
    this.upsellNext = this.querySelector('.cart-upsell-slide-button[name="next"]');
    this.upsellToggle = this.querySelector('#cart-upsell-toggle');

    this.upsellPrev.addEventListener('click', this.onUpsellPrevClick.bind(this));
    this.upsellNext.addEventListener('click', this.onUpsellNextClick.bind(this));
    this.upsellToggle.addEventListener('click', this.onToggleUpsell.bind(this));
  }

  setVariantSelectors() {
    this.upsellElement.querySelectorAll('.card-product-wrapper').forEach((card) => {
      const cardImage = card.querySelector('img');
      
      card.querySelector('.card-product-variant-selector')?.addEventListener('change', (event) => {
        const option = event.target.selectedOptions.item(0);

        if (option.dataset.variantImg) {
          const imageSrc = "/cdn/shop/" + option.dataset.variantImg;
          
          cardImage.srcset = cardImage.srcset.replaceAll(/\/+cdn[^\s]*/g, imageSrc);
          cardImage.src = imageSrc;
        }
      });
    });
  }

  onUpsellPrevClick() {
    if (this.upsellElement.scrollLeft === 0) {
      this.upsellElement.scrollLeft = this.upsellElement.scrollWidth;
    }
    else {
      this.upsellElement.scrollLeft -= this.upsellElement.clientWidth;
    }
  }

  onUpsellNextClick() {
    if (this.upsellElement.scrollLeft === (this.upsellElement.scrollWidth - this.upsellElement.clientWidth)) {
      this.upsellElement.scrollLeft = 0;
    }
    else {
      this.upsellElement.scrollLeft += this.upsellElement.clientWidth;
    }
  }

  onToggleUpsell() {
    const expanded = this.getAttributeNode("aria-expanded");
    
    if (expanded.value === "true") {
      expanded.value = "false" ;
    }
    else {
      expanded.value = "true";
    }
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);
